<template>
    <vue-scroll>
        <div class="float-left">
            <el-button
                @click="create"
                type="primary"
                v-if="!readonly && can(Permissions.CREATE_USER)"
            >
                <i class="mdi mdi-plus"></i> Create
            </el-button>
            <el-input
                placeholder="Search Users..."
                style="width: 250px"
                v-model="filter.search"
            />
        </div>
        <template v-if="filterable">
            <div class="float-right">
                Filter by:
                <el-select
                    style="width: 200px"
                    filterable
                    clearable
                    value-key="id"
                    v-model="filter.corporation"
                    placeholder="Corporation..."
                >
                    <el-option
                        v-for="item in corporations.data"
                        :key="item.id"
                        :label="item.name"
                        :value="item"
                    />
                </el-select>
                <el-select
                    style="width: 200px"
                    filterable
                    clearable
                    value-key="id"
                    v-model="filter.community"
                    placeholder="Community..."
                >
                    <el-option
                        v-for="item in filteredCommunities"
                        :key="item.id"
                        :label="item.name"
                        :value="item"
                    />
                </el-select>
            </div>
            <div class="clearfix"></div>
        </template>
        <el-table
            :data="paginatedData"
            @sort-change="onSortChange"
            ref="table"
            row-key="id"
        >
            <el-table-column label="Name" prop="name" sortable="custom" />
            <el-table-column label="Email" prop="email" />
            <el-table-column label="Username" prop="username" />
            <el-table-column label="Phone Number" prop="phone_number" />
            <el-table-column
                label="Role"
                prop="role_description"
                sortable="custom"
            />
            <el-table-column
                label="Active"
                prop="is_active"
                sortable="custom"
                width="100px"
            >
                <template slot-scope="scope">
                    <i
                        class="mdi mdi-check success-text"
                        v-if="scope.row.is_active"
                    />
                    <i class="mdi mdi-close danger-text" v-else />
                </template>
            </el-table-column>
            <el-table-column
                prop="actions"
                v-if="
                    !readonly ||
                    hasRole(Roles.CORPORATION_OWNER) ||
                    hasRole(Roles.MAIN_ROLES)
                "
                width="240px"
            >
                <template slot-scope="scope">
                    <el-button-group>
                        <el-button
                            @click="editItem(scope.row)"
                            icon="mdi mdi-pencil"
                            size="small"
                            v-if="
                                can(Permissions.EDIT_USER) ||
                                hasRole(Roles.CORPORATION_OWNER) ||
                                hasRole(Roles.MAIN_ROLES)
                            "
                            >Edit
                        </el-button>
                        <el-button
                            :loading="
                                isItemBusy(scope.row, 'send password reset')
                            "
                            @click="sendPasswordReset(scope.row)"
                            icon="mdi mdi-send"
                            size="small"
                            type="danger"
                            v-if="scope.row"
                            >Reset PW
                        </el-button>
                    </el-button-group>
                </template>
            </el-table-column>
        </el-table>
        <div class="mt-10" v-if="pageCount >= 1">
            <el-pagination
                class="pull-right"
                v-if="pagination"
                background
                :page-size="pagination.per_page"
                :current-page.sync="pagination.current_page"
                :total="dataCount"
                @current-change="changePage"
                @size-change="handleSizeChange"
            />
            <div class="clearfix"></div>
        </div>
        <el-dialog
            :close-on-click-modal="false"
            :title="editingItem ? 'Edit User' : 'Create User'"
            :visible.sync="dialogVisible"
            width="500px"
            v-if="!readonly || hasRole(Roles.CORPORATION_OWNER)"
        >
            <el-form @submit.prevent="submit()">
                <el-form-item required label="Name" :class="form.state('name')">
                    <el-input name="name" v-model="form.name" />
                    <form-feedback :form="form" field="name" />
                </el-form-item>
                <el-form-item
                    required
                    label="Email"
                    :class="form.state('email')"
                >
                    <el-input name="email" v-model="form.email" />
                    <form-feedback :form="form" field="email" />
                </el-form-item>
                <el-form-item
                    label="Phone Number"
                    :class="form.state('phone_number')"
                >
                    <el-input name="phone_number" v-model="form.phone_number" />
                    <form-feedback :form="form" field="phone_number" />
                </el-form-item>
                <el-form-item
                    label="Job Title"
                    :class="form.state('job_title')"
                >
                    <el-input name="job_title" v-model="form.job_title" />
                    <form-feedback :form="form" field="job_title" />
                </el-form-item>
                <el-form-item :class="form.state('username')" label="Username">
                    <el-input name="username" v-model="form.username" />
                    <form-feedback :form="form" field="username" />
                </el-form-item>
                <el-form-item
                    :class="form.state('password')"
                    :required="!editingItem"
                    label="Password"
                >
                    <el-input
                        autocomplete="new-password"
                        show-password
                        type="password"
                        v-model="form.password"
                    />
                    <form-feedback :form="form" field="password" />
                </el-form-item>
                <el-form-item
                    required
                    label="Role"
                    :class="form.state('role')"
                    v-if="!readonly"
                >
                    <el-select
                        filterable
                        placeholder="Select"
                        v-model="form.role"
                    >
                        <el-option-group label="Main Roles">
                            <el-option
                                v-for="role in MainRoles"
                                :key="role"
                                :label="getRoleName(role)"
                                :value="role"
                            />
                        </el-option-group>
                        <el-option-group label="Client Roles">
                            <el-option
                                :value="ClientRoles.CORPORATION_OWNER"
                                label="Corporation Owner"
                            />
                            <el-option
                                :value="ClientRoles.COMMUNITY_MANAGER"
                                label="Community Manager"
                            />
                        </el-option-group>
                    </el-select>
                    <form-feedback :form="form" field="role" />
                </el-form-item>
                <el-form-item
                    v-if="form.role === Roles.CORPORATION_OWNER"
                    label="Corporation"
                    :class="form.state('parent_id')"
                >
                    <el-select
                        filterable
                        clearable
                        v-model="form.parent_id"
                        placeholder="Select"
                    >
                        <el-option
                            v-for="item in corporations.data"
                            :key="item.id"
                            :label="item.name"
                            :value="item.id"
                        />
                    </el-select>
                    <form-feedback :form="form" field="parent_id" />
                </el-form-item>
                <el-form-item
                    :class="form.state('parent_ids')"
                    label="Community"
                    v-if="form.role === Roles.COMMUNITY_MANAGER"
                >
                    <select-community
                        :initial-options="initialCommunityOptions"
                        v-model="form.parent_ids"
                    />
                    <form-feedback :form="form" field="parent_ids" />
                </el-form-item>
                <el-row>
                    <el-col :span="12">
                        <el-form-item
                            label="Active"
                            :class="form.state('is_active')"
                        >
                            <el-switch v-model="form.is_active" />
                            <form-feedback :form="form" field="is_active" />
                        </el-form-item>
                    </el-col>
                    <el-col
                        :span="12"
                        class="text-right assignable-user"
                        v-if="hasRole(Roles.MAIN_ROLES)"
                    >
                        <el-form-item
                            label="Assignable"
                            :class="form.state('is_assignable')"
                        >
                            <el-switch
                                v-model="form.is_assignable"
                                class="d-inline-flex"
                            />
                            <form-feedback :form="form" field="is_assignable" />
                        </el-form-item>
                    </el-col>
                </el-row>
            </el-form>
            <div slot="footer" class="dialog-footer">
                <div
                    class="float-left"
                    v-if="editingItem && can(Permissions.DELETE_USER)"
                >
                    <el-button
                        round
                        type="danger"
                        icon="mdi mdi-delete"
                        @click="deleteUser()"
                        :loading="isItemBusy(editingItem, 'delete')"
                    >
                        Delete User
                    </el-button>
                </div>
                <div class="float-right">
                    <el-button
                        @click="dialogVisible = false"
                        :disabled="form.busy"
                        >Cancel</el-button
                    >
                    <el-button
                        type="primary"
                        icon="mdi mdi-content-save"
                        @click="submit"
                        :loading="form.busy"
                        >Save
                    </el-button>
                </div>
                <div class="clearfix"></div>
            </div>
        </el-dialog>
    </vue-scroll>
</template>

<script>
import SelectCommunity from "@/components/SelectCommunity";
import * as ClientRoles from "@/constants/client-roles";
import * as MainRoles from "@/constants/main-roles";
import * as Roles from "@/constants/roles";
import auth_mixin from "@/mixins/auth";
import paginator from "@/mixins/frontend-paginator";
import unsaved_changes_mixin from "@/mixins/unsaved-changes";
import password_reset_mixin from "@/mixins/user/password-reset.js";
import * as Actions from "@/store/constants/actions";
import * as Mutations from "@/store/constants/mutations";
import { mapState } from "vuex";

export default {
    mixins: [
        auth_mixin,
        paginator,
        unsaved_changes_mixin,
        password_reset_mixin
    ],
    components: {
        SelectCommunity
    },
    props: {
        readonly: {
            type: Boolean,
            default: false
        },
        corporation: {
            type: Object,
            required: false
        },
        filterable: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            Roles,
            MainRoles,
            ClientRoles,
            dialogVisible: false,
            form: new Form({
                name: null,
                email: null,
                username: null,
                phone_number: null,
                role: null,
                is_active: true,
                is_assignable: false,
                parent_id: null,
                parent_ids: [],
                password: null,
                job_title: null
            }),
            editingItem: null,
            filter: {
                corporation: null,
                community: null,
                search: ""
            },
            dataKey: "filteredItems",
            oldData: null,
            sortBy: null,
            sortOrder: "asc",
            initialCommunityOptions: []
        };
    },
    mounted() {},
    computed: {
        ...mapState({
            items: (state) => state.users
        }),
        ...mapState(["corporations", "communities"]),
        filteredCommunities() {
            if (!this.filter.corporation) {
                return this.communities.data;
            }
            return this.communities.data.filter((item) => {
                return item.corporation_id === this.filter.corporation.id;
            });
        },
        filteredItems() {
            let items = this.items.data.filter((user) => {
                let role = user.roles.data[0];
                if (this.filter.corporation) {
                    if (!role) {
                        return false;
                    }
                    if (role.parent_id) {
                        if (role.parent_type === "App\\Community") {
                            // community might already been deleted
                            let community = this.communities.data.find(
                                (item) => item.id === role.parent_id
                            );
                            if (
                                !community ||
                                community.corporation_id !==
                                    this.filter.corporation.id
                            ) {
                                return false;
                            }
                        } else if (
                            role.parent_id !== this.filter.corporation.id
                        ) {
                            return false;
                        }
                    } else {
                        return false;
                    }
                }
                if (this.filter.community) {
                    if (!role) {
                        return false;
                    }
                    if (role.parent_id) {
                        if (
                            role.parent_type !== "App\\Community" ||
                            role.parent_id !== this.filter.community.id
                        ) {
                            return false;
                        }
                    } else {
                        return false;
                    }
                }
                if (
                    this.filter.search &&
                    !window.Utils.emptyOrNull(this.filter.search)
                ) {
                    return (
                        (user.name &&
                            user.name
                                .toLowerCase()
                                .includes(this.filter.search.toLowerCase())) ||
                        (user.email &&
                            user.email
                                .toLowerCase()
                                .includes(this.filter.search.toLowerCase()))
                    );
                }
                return true;
            });
            if (this.sortOrder) {
                items = _.orderBy(items, [this.sortBy], [this.sortOrder]);
            }
            return _.values(items);
        },
        hasChanges() {
            return (
                this.dialogVisible &&
                this.oldData !== JSON.stringify(this.getCleanFormData())
            );
        },
        editingItemHasCommunityManagerRole() {
            if (!this.editingItem) {
                return false;
            }
            return _.find(this.editingItem.roles.data, {
                role: Roles.COMMUNITY_MANAGER
            });
        }
    },
    methods: {
        initOldData() {
            this.oldData = JSON.stringify(this.getCleanFormData());
        },
        getCleanFormData() {
            return _.pick(this.form, [
                "name",
                "email",
                "username",
                "is_active",
                "is_assignable",
                "phone_number",
                "role",
                "parent_id",
                "job_title"
            ]);
        },
        create() {
            this.initialCommunityOptions = [];
            this.editingItem = null;
            this.dialogVisible = true;
            this.form.clearFields();
            this.initOldData();
        },
        submit() {
            if (this.editingItem) {
                let fields = _.keys(this.form);
                if (this.editingItemHasCommunityManagerRole) {
                    fields = _.reject(_.keys(this.form), (key) =>
                        _.includes(["is_default_contact"], key)
                    );
                }
                window.App.put(
                    `/web-api/user/${this.editingItem.id}?include=${this.items.meta.include}`,
                    this.form,
                    null,
                    fields
                ).then((response) => {
                    this.dialogVisible = false;
                    this.$notify.success({
                        message: `Changes have been saved`
                    });
                    // todo: need better implementation
                    this.$store.dispatch(Actions.FETCH_USERS);
                    // this.$store.commit(Mutations.UPDATE_USER, response.data);
                });
            } else {
                window.App.post(
                    `/web-api/user?include=${this.items.meta.include}`,
                    this.form
                ).then((response) => {
                    this.dialogVisible = false;
                    this.$notify.success({
                        message: `New user has been saved.`
                    });
                    // todo: need better implementation
                    this.$store.dispatch(Actions.FETCH_USERS);
                    // this.$store.commit(Mutations.NEW_USER, response.data)
                });
            }
        },
        editItem(item) {
            this.form.clearFields();
            this.initialCommunityOptions = [];
            axios
                .get(`/web-api/user/${item.id}`, {
                    params: {
                        include: "roles.parent"
                    }
                })
                .then((response) => {
                    let user = response.data;
                    _.extend(
                        this.form,
                        _.pick(user, [
                            "name",
                            "email",
                            "username",
                            "is_active",
                            "is_assignable",
                            "phone_number",
                            "job_title"
                        ])
                    );
                    this.form.role = _.get(user, "roles.data.0.role");
                    if (this.form.role === Roles.COMMUNITY_MANAGER) {
                        _.forEach(user.roles.data, (role) => {
                            this.form.parent_ids.push(role.parent_id);
                            this.initialCommunityOptions.push(role.parent);
                        });
                    } else {
                        this.form.parent_id = _.get(
                            user,
                            "roles.data.0.parent_id"
                        );
                    }
                    this.editingItem = item;
                    this.dialogVisible = true;
                    this.initOldData();
                });
        },
        deleteUser() {
            this.$confirm("Are you sure you want to delete user?", "Warning", {
                confirmButtonText: "Delete",
                confirmButtonClass: "el-button--danger",
                cancelButtonText: "Cancel",
                type: "warning"
            })
                .then(() => {
                    this.addBusyItem(this.editingItem, "delete");
                    axios
                        .delete(`/web-api/user/${this.editingItem.id}`)
                        .finally(() => {
                            this.removeBusyItem(this.editingItem, "delete");
                        })
                        .then(() => {
                            this.$store.commit(
                                Mutations.REMOVE_USER,
                                this.editingItem
                            );
                            this.editingItem = null;
                            this.$notify.success({
                                message: "User has been deleted."
                            });
                            this.dialogVisible = false;
                        });
                })
                .catch(() => {});
        },

        onSortChange({ prop, order }) {
            let sort_order = null;
            let sort_by = prop;
            if (order) {
                sort_order = order === "descending" ? "desc" : "asc";
            } else {
                sort_by = null;
            }
            this.sortBy = sort_by;
            this.sortOrder = sort_order;
        }
    },
    watch: {
        "filter.corporation"() {
            if (this.filter.corporation) {
                this.filter.community = null;
            }
        }
    }
};
</script>

<style>
.assignable-user .el-form-item__label {
    float: none;
}
.assignable-user .el-form-item__content {
    display: inline-block;
}
.default-user .el-form-item__label {
    float: none;
}

.default-user .el-form-item__content {
    display: inline-block;
}
</style>
